import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Lock } from "lucide-react";
import { openRazorpay } from "@/lib/razorpay";
import StatusBadge from "@/components/StatusBadge";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [oid, setOid] = useState(params.get("id") || "");
  const [pin, setPin] = useState("");
  const [data, setData] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => { if (params.get("id")) track(params.get("id")); }, [params]);

  const track = async (idArg) => {
    const id = idArg || oid;
    if (!id) return toast.error("Enter an order id");
    try { const r = await api.get(`/orders/track/${id}`); setData(r.data); }
    catch { toast.error("Order not found"); }
  };
  const checkPin = async () => {
    try { const r = await api.post("/delivery/check", { pincode: pin }); setDelivery(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid pincode"); }
  };
  const payBalance = async () => {
    setPaying(true);
    try {
      const { data: intent } = await api.post(`/payments/bespoke/balance/create/${data.order_id}`);
      await openRazorpay(intent, {
        name: "Squire & Squire",
        description: `Balance — ${data.order_id}`,
        onSuccess: async (resp) => {
          try { await api.post("/payments/bespoke/balance/verify", { intent_id: intent.intent_id, ...resp }); toast.success("Balance paid in full!"); track(data.order_id); }
          catch (e) { toast.error("Payment could not be verified."); }
          finally { setPaying(false); }
        },
        onDismiss: () => { setPaying(false); toast("Payment cancelled"); },
      });
    } catch (e) { setPaying(false); toast.error(e?.response?.data?.detail || "Could not start payment"); }
  };

  return (
    <main className="sq-container py-12" data-testid="track-order">
      <div className="sq-eyebrow text-squire-jeans">Shiprocket Tracking</div>
      <h1 className="font-display text-5xl md:text-6xl mt-2">Track Your Order</h1>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl">
        <div>
          <div className="sq-eyebrow">Order ID</div>
          <div className="flex gap-2 mt-2">
            <input data-testid="track-input" value={oid} onChange={(e) => setOid(e.target.value)} placeholder="BORD00001" className="flex-1 bg-transparent border-b border-black/30 outline-none py-2" />
            <button data-testid="track-btn" onClick={() => track()} className="sq-btn-primary">Track</button>
          </div>
          {data && (
            <div className="mt-8 space-y-4" data-testid="track-result">
              <div className="border border-black/10 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="sq-eyebrow">Status</div>
                  <StatusBadge status={data.shipping_status} />
                </div>
                {data.awb ? (
                  <div className="mt-3 text-sm space-y-1">
                    <div>AWB: <strong>{data.awb}</strong></div>
                    {data.courier && <div className="text-[#4B5563]">Courier: {data.courier}</div>}
                    {data.tracking_url && <a href={data.tracking_url} target="_blank" rel="noreferrer" data-testid="track-shipment-link" className="inline-block mt-2 text-squire-jeans underline">Track shipment on courier ↗</a>}
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-[#4B5563]">A tracking number appears here once your order is dispatched.</div>
                )}
                {data.estimated_delivery && <div className="text-xs text-[#4B5563] mt-2">Estimated delivery: <strong className="text-black">{data.estimated_delivery.slice(0, 10)}</strong></div>}
              </div>

              <div className="space-y-3 mt-2">
                {data.stages.map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {s.done ? <CheckCircle2 className="text-squire-jeans" size={20} /> : <Circle className="text-[#D1D5DB]" size={20} />}
                    <div>
                      <div className={`font-medium ${s.done ? "" : "text-[#9CA3AF]"}`}>{s.stage}</div>
                      {s.at && <div className="text-xs text-[#4B5563]">{s.at.slice(0, 16).replace("T", " ")}</div>}
                    </div>
                  </div>
                ))}
              </div>

              {data.balance_due > 0 && (
                <div className="mt-6 border border-squire-jeans/30 bg-squire-jeans/5 p-4" data-testid="track-balance">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Balance due{data.balance_method === "cod" ? " · Cash on Delivery" : ""}</div>
                      <div className="text-xs text-[#4B5563]">Paid {formatINR(data.advance_paid)} of {formatINR(data.total)}. {data.balance_method === "cod" ? "Payable in cash to the courier on delivery." : "Payable on delivery."}</div>
                    </div>
                    <div className="font-display text-xl">{formatINR(data.balance_due)}</div>
                  </div>
                  {data.balance_method === "cod" ? (
                    <div className="mt-3 text-xs text-[#4B5563]">You chose Cash on Delivery — hand {formatINR(data.balance_due)} to the delivery agent. Prefer to pay online instead?
                      <button data-testid="track-pay-balance" onClick={payBalance} disabled={paying} className="ml-1 text-squire-jeans underline">{paying ? "Opening…" : "Pay online now"}</button>
                    </div>
                  ) : (
                    <button data-testid="track-pay-balance" onClick={payBalance} disabled={paying} className="sq-btn-primary w-full justify-center mt-3">
                      <Lock size={14} /> {paying ? "Opening payment…" : `Pay Balance ${formatINR(data.balance_due)}`}
                    </button>
                  )}
                </div>
              )}
              {data.balance_due === 0 && data.payment_status === "paid" && (
                <div className="mt-6 text-sm text-green-700 font-medium" data-testid="track-paid">✓ Paid in full</div>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="sq-eyebrow">Check Delivery by Pincode</div>
          <div className="flex gap-2 mt-2">
            <input data-testid="pin-check-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="6-digit pincode" className="flex-1 bg-transparent border-b border-black/30 outline-none py-2" />
            <button data-testid="pin-check-btn" onClick={checkPin} className="sq-btn-outline">Check</button>
          </div>
          {delivery && <div className="mt-4 text-sm text-[#4B5563]" data-testid="pin-check-result">Delivers by <strong className="text-black">{delivery.eta_date}</strong> via {delivery.courier}.</div>}

          <div className="mt-10 border-t border-black/10 pt-6 text-xs text-[#4B5563] leading-relaxed">
            <div className="sq-eyebrow text-black mb-2">How tracking works</div>
            Once your order is paid it goes into tailoring. When it's dispatched we hand it to Shiprocket, who assign a courier and an AWB tracking number — that appears here with a live tracking link. You'll move through Dispatched → Out for Delivery → Delivered, and can pay any balance from this page.
          </div>
        </div>
      </div>
    </main>
  );
}
